import React from 'react';

import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/labels/DataQualityType';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { DataSources } from '~src/datasource/resources/Datasources';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { CardTitle } from '~src/spacesPlatform/components/card/CardTitle';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardPopoverWidget, ContentCard, ContentCardProps, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ContentTooltipProps } from '~src/spacesPlatform/components/tooltip/TooltipHelper';

import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { IFavoritableItemInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { IDataSourceInfo } from '../../models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../models/mappers/CommonDatasourceInfo';
import { DatasourceTooltip } from '../tooltips/DatasourceTooltip';
import { ConnectedDetailedDatasourceThumbnail } from './ConnectedDetailedDatasourceThumbnail';

export const getDatasourceDefaultImageUrl = () => imgPath('data-source-icon-loading.svg');

const CertificationCardStyle: React.CSSProperties = {
  paddingTop: '3px',
  marginLeft: '-9px',
  marginRight: '-3px',
};

export class DatasourceCard extends ContentCard {

  private isUnifiedDatasource(): boolean {
    return DataSources.isUnifiedDatasource(this.props.content);
  }

  private getDatasourceId(): string {
    if (this.isUnifiedDatasource()) {
      return (this.props.content as IDataSourceInfo).idForFavoriting;
    }
    else {
      return this.props.content.id;
    }
  }

  public render() {
    const {
      content, urls, inWorkbookDatasourcesSubspace, allowDragAndDrop,
      hovered, selected, addContentFavorite, removeContentFavorite, inRecycleBinSpace
    } = this.props;
    /*
      Card does not navigate or show FavoriteToggle and PopOver when in Recycle bin space.
    */
    const contentUrl = getDatasourceContentUrl(urls, content, inWorkbookDatasourcesSubspace);

    let thumbnail: JSX.Element = null;
    if (hasExtraDatasourceDetail(content)) {
      thumbnail = DetailedDatasourceThumbnail(contentUrl, this.props, this.state.cardZoomPercentage);
    } else {
      thumbnail = <Thumbnail
        navigateUrl={inRecycleBinSpace ? null : contentUrl}
        defaultImageUrl={getDatasourceDefaultImageUrl()}
        allowDragAndDrop={allowDragAndDrop}
        hovered={hovered}
        selected={selected}
        aria-label={content.name} />;
    }

    const commonDatasource = getCommonDatasourceInfo(content);

    const favoritableItemInfo: IFavoritableItemInfo = {
      ...content,
      contentType: 'datasource',
      idForFavoriting: this.getDatasourceId(),
    };

    const certificationsExist = DataSources.isPublished(commonDatasource) ? commonDatasource.dataQualityIndicators?.filter(i => i.type === CERTIFIED_INDICATOR_TYPE)[0] ?? null : null;

    return (
      <div className={CardViewStyles.Card}>
        {thumbnail}
        <div className={CardViewStyles.ActionRow} style={this.getCardZoomStyleForActionRow()}>
          {certificationsExist ?
            <LabelWrapper itemInfo={content as IContentInfo<'datasource'>} category={'certification'} isEager={false} kind={'icon'}
              wrapper={(e) => <div data-tb-test-id='datasource-card-certification' style={CertificationCardStyle}>{e}</div>} />
            : null
          }
          <CardTitle name={content.name} data-tb-test-id="datasource-name" />
          {!inRecycleBinSpace && <span>
            <CardPopoverWidget
              content={content}
              onTriggerClick={this.handleShowPopover}
              popoverContent={this.renderPopoverContent()}
              title={Messages.datasourceDetails()}
            />
          </span>
          }
          {!DataSources.isEmbedded(commonDatasource) && !inRecycleBinSpace &&
            <span>
              <FavoriteToggle isTabbable={true}
                isFavorite={content.favorite}
                itemName={content.name}
                onAddFavorite={() => addContentFavorite(favoritableItemInfo)}
                onRemoveFavorite={() => removeContentFavorite(favoritableItemInfo)} />
            </span>
          }
          {this.renderActionsMenu()}
        </div>
        {this.shouldRenderExtraInformationAtBottom() && <div className={CardViewStyles.CardDescription}>
          {this.getDescription()}
        </div>}
      </div>
    );
  }

  protected getPopoverComponent(): React.ComponentType<ContentTooltipProps> {
    return DatasourceTooltip;
  }
}

export function getDatasourceContentUrl(
  urls: SpacesSiteUrls,
  content: IContentInfo,
  inWorkbookDatasourcesSubspace: boolean
): string {
  const commonDatasource = getCommonDatasourceInfo(content);

  const workbook = DataSources.isEmbedded(commonDatasource) ? commonDatasource.workbook : undefined;
  if (workbook) {
    // If we're already in a workbook place, the card href would link us to where we already are,
    // so don't make it a link.
    if (inWorkbookDatasourcesSubspace) {
      return undefined;
    }

    return urls.workbookDatasourcesUrl(workbook.id);
  }

  if (DataSources.isUnifiedDatasource(content)) {
    return urls.datasourceUrl(content.id);
  }

  return getContentUrlForItem(content, urls);
}

function showExtractEncryptionStatus(): boolean {
  const sessionManagementService = getSessionManagementServiceInstance();
  return sessionManagementService.isExtractEncryptionEnabledOmniSwitch();
}

function isUnifiedDatasource(content: IContentInfo): boolean {
  return DataSources.isUnifiedDatasource(content);
}

// following functions are exported so they can be used as part of other mixed content cards (such as SharedWithMeCard)

export function hasExtraDatasourceDetail(content: IContentInfo): boolean {
  return isUnifiedDatasource(content) ||
    (content.contentType.toLowerCase() === 'datasource' && content.contentDetail);
}

export function DetailedDatasourceThumbnail(contentUrl: string, props: ContentCardProps, cardZoomPercentage: number, ariaLabel?: string): JSX.Element {
  let thumbnailModel = null;
  if (isUnifiedDatasource(props.content)) {
    thumbnailModel = getCommonDatasourceInfo(props.content as IDataSourceInfo);
  } else if (props.content.contentDetail) {
    thumbnailModel = getCommonDatasourceInfo(props.content as IContentInfo);
  }

  return (
    <ConnectedDetailedDatasourceThumbnail
      urls={props.urls}
      contentUrl={props.inRecycleBinSpace ? null : contentUrl}
      selected={props.selected}
      hovered={props.hovered}
      datasource={thumbnailModel}
      allowDragAndDrop={props.allowDragAndDrop}
      showExtractEncryptionStatus={showExtractEncryptionStatus()}
      aria-label={ariaLabel || props.content.name}
      cardZoomPercentage={cardZoomPercentage}
    />
  );
}
